import React, { FC, RefObject, useEffect, useState, forwardRef, useCallback } from 'react';
import { Collapse } from 'bootstrap';
import { SpaceCard, SpaceCardProps } from '@components/molecules/SpaceCard';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Pagination } from '@components/molecules/Pagination';
import { ResultsType } from '@types';
import { ResultsPageTop, ResultsPageTopProps } from './ResultsPageTop';
import { SearchResultsHeader } from './SearchResultsHeader';
import styles from './ResultsContainer.module.css';
import { MapContainer, Marker, TileLayer } from 'react-leaflet';
import { icon } from 'leaflet';
import mapPinIcon from '@assets/images/map-pin.svg'

const pinIcon = icon({
    iconUrl: mapPinIcon,
    iconSize:     [38, 95],
    shadowSize:   [50, 64],
    iconAnchor:   [22, 94],
    shadowAnchor: [4, 62],
    popupAnchor:  [-3, -76]
});

interface ResultsContainerProps {
    type: ResultsType;
    results: SpaceCardProps[];
    scrollObserverRef?: RefObject<HTMLDivElement> | undefined;
    filters: JSX.Element;
    total: number;
    order: ResultsPageTopProps['order'];
    searchQuery?: string;
    loading: boolean;
    currentPage?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
    isMapVisible?: boolean;
}

export const ResultsContainer = forwardRef<HTMLDivElement, ResultsContainerProps>(({
                                                                type,
                                                                results,
                                                                total,
                                                                order,
                                                                searchQuery,
                                                                loading,
                                                                filters,
                                                                currentPage,
                                                                totalPages,
                                                                onPageChange,
                                                                isMapVisible = true,
                                                            }, ref) => {
    const [sidebar] = useState<Collapse | null>(null);
    const [isMobile, setIsMobile] = useState(false);

    const checkIsMobile = useCallback(() => {
        setIsMobile(window.innerWidth < 768);
    }, []);

    useEffect(() => {
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return () => window.removeEventListener('resize', checkIsMobile);
    }, [checkIsMobile]);

    const shouldShowMap = isMobile ? isMapVisible : true;

    useEffect(() => {
        // destroy the element when the component unmounts
        return () => {
            try {
                sidebar?.dispose();
            } catch (e) {
                console.log('catch error disposing of sidebar');
            }
        };
    }, [sidebar]);

    // const sidebarRef = useCallback((node: HTMLDivElement) => {
    //     if (node !== null) {
    //         const collapseInstance = Collapse.getOrCreateInstance(node, { toggle: false });
    //         setSidebar(collapseInstance);
    //     }
    // }, []);

    // const closeSidebar = () => {
    //     try {
    //         sidebar?.hide();
    //     } catch (e) {
    //         console.log('catch error closing sidebar');
    //     }
    // };

    const [pins, setPins] = useState<[number, number][]>([]);

    const definePins = (): void => {
        const arrCoords = results
            .filter(result => result.latitude && result.longitude)
            .map(result => {
                const lat = parseFloat(result.latitude);
                const lng = parseFloat(result.longitude);
                return [lat, lng] as [number, number];
            })
            .filter(([lat, lng]) => !isNaN(lat) && !isNaN(lng));
        setPins(arrCoords);
    };

    useEffect(() => {
        if (results.length) {
            definePins();
        } else {
            setPins([]);
        }
    }, [results]);

    return (
        <div ref={ref} data-testid="results-container">
            <Container classes={isMobile ? styles.mobileContainer : ''}>
                <>
                    {searchQuery && <SearchResultsHeader searchQuery={searchQuery} />}

                    {/* Filters - Full width at top */}
                    <div className={`row ${isMobile ? styles.mobileFiltersRow : 'mt-4'}`}>
                        <div className="col-12 mb-4">{filters}</div>
                    </div>

                    <ResultsPageTop type={type} total={total} order={order} sidebar={sidebar} />

                    {!loading && results.length === 0 ? (
                        <div className="row">
                            <div className="col-12 d-flex flex-column align-items-center text-center py-5">
                                <Typography
                                classes='mb-3'
                                    tagVariant={TypographyTagVariants.h2}
                                    fontSize="24"
                                    fontWeight="900"
                                >
                                    Não encontrou o que procurava?
                                </Typography>
                                <Typography fontSize="16" classes="mb-4" color="muted">
                                    Subscreva as notificações automáticas para novos imóveis ou negócios.
                                </Typography>
                                <Button mode={ButtonMode.primary} label="SUBSCREVER" />
                            </div>
                        </div>
                    ) : (
                        <div className="row">
                            {/* Map - Only visible on desktop (mobile map is in SpacesFilter) */}
                            {!isMobile && (
                                <div className="col-12 col-lg-6 order-lg-2 mb-4 mb-lg-0">
                                    {!!pins.length && (
                                        <div className={styles.mapContainer}>
                                            <MapContainer center={pins[0]} zoom={7}
                                                          scrollWheelZoom={false}>
                                                <TileLayer
                                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                />

                                                {pins.map((pin, idx) => (
                                                    <Marker icon={pinIcon} key={idx} position={pin}>
                                                        <span>Marker custom</span>
                                                    </Marker>
                                                ))}
                                                <Marker position={[51.505, -0.09]}>
                                                    <span>Marker custom</span>
                                                </Marker>
                                            </MapContainer>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Results Section */}
                            <div className={`col-12 ${!isMobile ? 'col-lg-6' : 'col-lg-12'} order-lg-1`}>
                                <div className="row">
                                    {(results as SpaceCardProps[]).map((item) => (
                                        <div key={`result-${item.uuid}`} className="d-grid col-12 col-sm-6 col-lg-6 mb-4">
                                            <SpaceCard {...item} />
                                        </div>
                                    ))}
                                </div>
                                <Pagination
                                    loading={loading}
                                    type={type}
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={onPageChange}
                                />
                            </div>
                        </div>
                    )}
                </>
            </Container>
        </div>
    );
});

ResultsContainer.displayName = 'ResultsContainer';
